import { useState, useEffect } from 'react';

const RUSSIAN_TIMEZONES = new Set([
  'Europe/Moscow',
  'Europe/Samara',
  'Europe/Volgograd',
  'Europe/Saratov',
  'Europe/Ulyanovsk',
  'Europe/Kirov',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Novosibirsk',
  'Asia/Barnaul',
  'Asia/Tomsk',
  'Asia/Novokuznetsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Chita',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Magadan',
  'Asia/Sakhalin',
  'Asia/Srednekolymsk',
  'Asia/Kamchatka',
  'Asia/Anadyr',
]);

let moduleCache: boolean | null = null;

async function fetchCountryCode(signal: AbortSignal): Promise<string | null> {
  const services: Array<{ name: string; fn: () => Promise<string | null> }> = [
    {
      name: 'api.country.is',
      fn: async () => {
        const res = await fetch('https://api.country.is/', { signal });
        const data = await res.json();
        return typeof data.country === 'string' ? data.country : null;
      },
    },
    {
      name: 'ipinfo.io',
      fn: async () => {
        const res = await fetch('https://ipinfo.io/json', { signal });
        const data = await res.json();
        return typeof data.country === 'string' ? data.country : null;
      },
    },
    {
      name: 'get.geojs.io',
      fn: async () => {
        const res = await fetch('https://get.geojs.io/v1/ip/country.json', { signal });
        const data = await res.json();
        return typeof data.country === 'string' ? data.country : null;
      },
    },
    {
      name: 'ipapi.co',
      fn: async () => {
        const res = await fetch('https://ipapi.co/json/', { signal });
        const data = await res.json();
        return typeof data.country_code === 'string' ? data.country_code : null;
      },
    },
  ];

  for (const { fn } of services) {
    try {
      const code = await fn();
      if (code) return code;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') throw err;
    }
  }
  return null;
}

export function useVpnDetection() {
  const [isVpn, setIsVpn] = useState(false);
  const [isChecking, setIsChecking] = useState(moduleCache === null);

  useEffect(() => {
    if (moduleCache !== null) {
      setIsVpn(moduleCache);
      setIsChecking(false);
      return;
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const language = navigator.language ?? '';
    const isRussianUser = RUSSIAN_TIMEZONES.has(timezone) || language.startsWith('ru');

    if (!isRussianUser) {
      moduleCache = false;
      setIsVpn(false);
      setIsChecking(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    fetchCountryCode(controller.signal)
      .then((countryCode) => {
        if (controller.signal.aborted) return;
        const detected = countryCode !== null && countryCode !== 'RU';
        moduleCache = detected;
        setIsVpn(detected);
        setIsChecking(false);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        moduleCache = false;
        setIsVpn(false);
        setIsChecking(false);
      });

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, []);

  return { isVpn, isChecking };
}
